<template>
  <div
    ref="viewerRootRef"
    class="translation-whiteboard"
    :class="{ 'mobile-view': isMobileView }"
    :style="viewerRootStyle"
  >
    <!-- H5：顶部目标语言切换按钮 -->
    <div
      v-if="isMobileView && targetLanguageList.length"
      class="viewer-lang-tabs"
    >
      <button
        v-for="t in targetLanguageList"
        :key="t.languageCode"
        type="button"
        class="lang-tab"
        :class="{ active: selectedTargetCode === t.languageCode }"
        @click="selectedTargetCode = t.languageCode"
      >
        {{ formatLangDisplayLabel(t) }}
      </button>
    </div>

    <!-- PC：仅展示各目标语言译文（不展示源语言）；底部可附「识别中」实时行 -->
    <template v-if="!isMobileView">
      <div v-if="hasViewerContent" class="viewer-table">
        <div class="viewer-cols-header" :style="viewerGridStyle">
          <div
            v-for="col in viewerColumnsMeta"
            :key="col.code"
            class="viewer-col-header"
          >
            {{ col.label }}
          </div>
        </div>
        <!-- PC：固定 vh 高度 + 底对齐 + 溢出裁切，多出行从顶部顶出画面，无滚动条、不做行数 JS 估算 -->
        <div class="viewer-rows-clip">
          <div class="viewer-rows">
            <div
              v-for="(row, idx) in displayFragments"
              :key="`${row.ts ?? 'row'}-${idx}`"
              class="viewer-row"
              :style="viewerGridStyle"
            >
              <div
                v-for="t in targetLanguageList"
                :key="`${row.ts ?? 'row'}-${idx}-${t.languageCode}`"
                class="viewer-line"
              >
                {{ formatViewerRowLineText(row, t.languageCode) }}
              </div>
            </div>
            <div
              v-if="previewLive && !isPreviewPayloadEmpty(previewLive)"
              :key="'live-preview-row'"
              class="viewer-row viewer-row--live"
              :style="viewerGridStyle"
            >
              <div
                v-for="t in targetLanguageList"
                :key="'live-' + t.languageCode"
                class="viewer-line viewer-line--live"
              >
                {{ formatViewerRowLineText(previewLive, t.languageCode) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- H5：单列当前目标语言 + 可选实时行；与 PC 相同 vh 区内裁切、底对齐顶掉顶部，无滚动条 -->
    <template v-else>
      <div class="mobile-body-panel">
        <div v-if="hasViewerContent" class="viewer-h5-table">
          <div class="viewer-rows-clip">
            <div class="viewer-rows viewer-rows--h5-single">
              <div
                v-for="(line, idx) in displayLinesSingle"
                :key="idx"
                class="viewer-line"
              >
                {{ line }}
              </div>
              <div
                v-if="livePreviewLineText !== ''"
                :key="'h5-live'"
                class="viewer-line viewer-line--live"
              >
                {{ livePreviewLineText }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <a-spin v-if="loading" class="loading-overlay" size="large" />

    <!-- V3 课后 Overlay：不断开 WS（D29）；Web 端暂不需要课后资料 QR -->
    <CrV3PostClassDrawer
      v-if="meetingCode"
      :session="postClassSession"
      :viewer-lang-codes="postClassViewerLangCodes"
      :source-lang-code="postClassSourceLangCode"
      :enable-qr-download="isMobileView"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { isMobileDevice } from '@/utils/common'
import { getCorrectedApiBaseUrl } from '@/shared/api/api-base'
import { publicFileAccessUrl } from '../api/session'
import type { ViewerLanguageItem } from '../api/viewer'
import { MAX_DISPLAY_LINES } from '../constants'
import { formatLangDisplayLabel } from '../utils/lang-display-label'
import { useCrV3ViewerFeed } from '@/modules/cr-v3-viewer/composables/useCrV3ViewerFeed'
import { useCrV3H5Feed } from '@/modules/cr-v3-viewer/composables/useCrV3H5Feed'
import { readJoinSession } from '@/modules/cr-v3-viewer/composables/useCrV3JoinSession'
import { formatViewerRowLineText, shortLangCode } from '@/modules/cr-v3-viewer/adapters/live-speech-adapter'
import CrV3PostClassDrawer from '@/modules/cr-v3-viewer/components/CrV3PostClassDrawer.vue'
import type { CrV3SessionSnapshot, ViewerSubtitleRow } from '@/modules/cr-v3-viewer/types'

export interface SubtitleStyleState {
  backgroundColor: string
  opacity: number
  fontColor: string
  fontSize: number
  fontFamily: string
  lineSpacing?: number
  backgroundImagePC?: string
  backgroundImageH5?: string
}

const props = defineProps<{
  /** V2 会议翻译 SSE 观众码 */
  userCode?: string
  /** CR V3 会议码：走 WebSocket liveSpeech */
  meetingCode?: string
  /** H5 参会端：PCM PTT + TTS */
  h5Participant?: boolean
}>()

const loading = ref(false)
const viewerRootRef = ref<HTMLElement | null>(null)
const fragments = ref<ViewerSubtitleRow[]>([])
/** 识别中的临时句（主讲端 recognizing 推送），定稿后 fragment 写入并由服务端清空 */
const previewLive = ref<ViewerSubtitleRow | null>(null)

function isPreviewPayloadEmpty(p: { source_text?: string; translations?: Record<string, string> } | null): boolean {
  if (!p) return true
  if ((p.source_text || '').trim()) return false
  const tr = p.translations || {}
  return !Object.values(tr).some((x) => String(x || '').trim())
}
/** 源语言对象（来自 init/轮询，与 cr_language 一致） */
const sourceLanguageObj = ref<ViewerLanguageItem>({ languageCode: '' })
/** 目标语言对象数组 */
const targetLanguageList = ref<ViewerLanguageItem[]>([])
/** H5 下当前选中的目标语言 code；PC viewer 默认第一列，亦用于课后 QR 语种匹配 */
const selectedTargetCode = ref('')
const isMobileView = ref(false)
/** V3 Gateway 课后快照（updateSessionData 浅合并） */
const postClassSession = ref<CrV3SessionSnapshot>({})

const postClassViewerLangCodes = computed(() => {
  const codes: string[] = []
  const short = selectedTargetCode.value
  if (short) {
    codes.push(short)
  }
  const item = targetLanguageList.value.find((t) => t.languageCode === short)
  if (item?.languageCode2) {
    codes.push(item.languageCode2)
  }
  // 当前所选语种与入会语种一致时，补 languageCode2（D23）
  if (props.meetingCode && short) {
    const join = readJoinSession(props.meetingCode)
    if (join?.languageCode && shortLangCode(join.languageCode) === shortLangCode(short)) {
      if (join.languageCode2) {
        codes.push(join.languageCode2)
      }
    }
  }
  return [...new Set(codes.filter(Boolean))]
})

const postClassSourceLangCode = computed(() => {
  return (
    sourceLanguageObj.value.languageCode2
    || sourceLanguageObj.value.languageCode
    || postClassSession.value.sourceLanguage?.languageCode2
    || postClassSession.value.sourceLanguage?.languageCode
    || postClassSession.value.workLangCode
    || ''
  )
})

// 字幕样式默认与 SubtitleSettings / 后端 DEFAULT_SUBTITLE_STYLE 一致（淡绿背景+黑字）；SSE init/subtitle 会覆盖
const subtitleStyle = ref<SubtitleStyleState>({
  backgroundColor: '#CCFFCC',
  opacity: 80,
  fontColor: '#000000',
  fontSize: 18,
  fontFamily: 'Microsoft YaHei',
  lineSpacing: 1.5
})
let eventSource: EventSource | null = null
/** SSE 断线重连：延迟（ms）、最大重试次数（0 表示不限制） */
const RECONNECT_DELAY_MS = 3000
const MAX_RECONNECT_ATTEMPTS = 10
const reconnectAttempts = ref(0)
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
const baseUrl = getCorrectedApiBaseUrl()

const subtitleCssVars = computed(() => {
  const s = subtitleStyle.value
  const opacity = s.opacity / 100
  const lineSpacing = s.lineSpacing ?? 1.5
  // 兼容后端/接口可能返回的 snake_case；根路径 /temp/... 在仅反代 /api 时需转为 /api/v1/temp/...
  const pcImg = publicFileAccessUrl(
    (s.backgroundImagePC ?? (s as any).background_image_pc)?.trim?.() ?? ''
  )
  const h5Img = publicFileAccessUrl(
    (s.backgroundImageH5 ?? (s as any).background_image_h5)?.trim?.() ?? ''
  )
  const isMobile = isMobileView.value
  const hasBgImage = isMobile ? !!h5Img : !!pcImg
  // 使用引号包裹 URL，避免含括号等字符时 CSS 解析错误
  const bgImage =
    isMobile ? (h5Img ? `url("${h5Img}")` : 'none') : (pcImg ? `url("${pcImg}")` : 'none')
  const bgColor = hasBgImage ? 'transparent' : s.backgroundColor
  return {
    '--subtitle-bg': bgColor,
    '--subtitle-bg-image': bgImage,
    '--subtitle-opacity': String(opacity),
    '--subtitle-color': s.fontColor,
    '--subtitle-size': `${s.fontSize}px`,
    '--subtitle-font': s.fontFamily,
    '--subtitle-line-height': String(lineSpacing)
  }
})

/** H5 侧栏与正文用 flex 分区，不再用 paddingTop 为 fixed 栏让位 */
const viewerRootStyle = computed(() => subtitleCssVars.value)

/** `fragments` 在 SSE 写入时已截断至 MAX_DISPLAY_LINES；PC/H5 一屏均由 CSS（flex-end + overflow:hidden）裁切，不再按条数切片 */
const displayFragments = computed(() => fragments.value)

const hasViewerContent = computed(
  () =>
    displayFragments.value.length > 0 ||
    (previewLive.value != null && !isPreviewPayloadEmpty(previewLive.value))
)

/** PC 表头列：仅各目标语言（观众端不展示源语言列） */
const viewerColumnsMeta = computed(() => {
  return targetLanguageList.value.map((t) => ({
    code: t.languageCode,
    label: formatLangDisplayLabel(t)
  }))
})

const viewerGridStyle = computed(() => ({
  gridTemplateColumns: viewerColumnsMeta.value.length
    ? `repeat(${viewerColumnsMeta.value.length}, minmax(0, 1fr))`
    : '1fr'
}))

/** H5 单列：当前选中目标语言的行列表 */
const displayLinesSingle = computed(() => {
  const code = selectedTargetCode.value
  if (!code) return []
  return displayFragments.value.map((f) => formatViewerRowLineText(f, code))
})

const livePreviewLineText = computed(() => {
  const code = selectedTargetCode.value
  if (!code || !previewLive.value || isPreviewPayloadEmpty(previewLive.value)) return ''
  return formatViewerRowLineText(previewLive.value, code)
})

/** 将接口返回的 sourceLanguage / targetLanguages 规范为对象格式（兼容旧版只传 code 的情况） */
function normalizeSourceLanguage(v: unknown): ViewerLanguageItem {
  if (v && typeof v === 'object' && 'languageCode' in v) {
    return v as ViewerLanguageItem
  }
  if (typeof v === 'string') {
    return { languageCode: v }
  }
  return { languageCode: '' }
}

function normalizeTargetLanguages(v: unknown): ViewerLanguageItem[] {
  if (!Array.isArray(v)) return []
  return v.map((item) => {
    if (item && typeof item === 'object' && 'languageCode' in item) {
      return item as ViewerLanguageItem
    }
    return { languageCode: String(item) }
  })
}

function applyInit(data: {
  fragments?: Array<{ source_text?: string; translations: Record<string, string> }>
  preview?: { source_text?: string; translations?: Record<string, string> } | null
  sourceLanguage?: unknown
  targetLanguages?: unknown
  subtitleStyle?: SubtitleStyleState
}) {
  if (data.fragments) {
    fragments.value = data.fragments.slice(-MAX_DISPLAY_LINES)
  }
  if (Object.prototype.hasOwnProperty.call(data, 'preview')) {
    const p = data.preview
    previewLive.value = p && !isPreviewPayloadEmpty(p) ? p : null
  }
  if (data.sourceLanguage != null) {
    sourceLanguageObj.value = normalizeSourceLanguage(data.sourceLanguage)
  }
  if (data.targetLanguages != null) {
    const list = normalizeTargetLanguages(data.targetLanguages)
    targetLanguageList.value = list
    if (list.length && !selectedTargetCode.value) {
      // V3：优先入会所选语种，便于课后 QR D23 匹配
      const joinLang = props.meetingCode
        ? readJoinSession(props.meetingCode)?.languageCode
        : ''
      const joinShort = joinLang ? shortLangCode(joinLang) : ''
      const preferred =
        joinShort
          ? list.find((t) => shortLangCode(t.languageCode) === joinShort)
          : undefined
      selectedTargetCode.value = (preferred ?? list[0]).languageCode
    }
  }
  if (data.subtitleStyle) {
    const raw = data.subtitleStyle as Record<string, unknown>
    subtitleStyle.value = {
      ...subtitleStyle.value,
      ...raw,
      backgroundImagePC: (raw.backgroundImagePC ?? raw.background_image_pc ?? '') as string,
      backgroundImageH5: (raw.backgroundImageH5 ?? raw.background_image_h5 ?? '') as string
    }
  }
}

/**
 * 判断是否使用 H5 布局；与 `join-gate.helpers.ts` 的路由分流共用 `isMobileDevice()`，
 * 避免两处标准不一致（Pad 用搜狗/QQ 浏览器打开时曾出现"内部渲染成 H5 样式，但路由到无按钮的观众端"问题）。
 */
function updateMobileView() {
  isMobileView.value = isMobileDevice()
}

function scheduleReconnect() {
  if (MAX_RECONNECT_ATTEMPTS > 0 && reconnectAttempts.value >= MAX_RECONNECT_ATTEMPTS) return
  reconnectAttempts.value += 1
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    connectSSE()
  }, RECONNECT_DELAY_MS)
}

function connectSSE() {
  if (!props.userCode) return
  if (eventSource) {
    eventSource.close()
    eventSource = null
  }
  const url = `${baseUrl}/speech-translation/user/${encodeURIComponent(props.userCode)}/stream`
  // 无活跃会话时后端会立即结束 SSE，前端会定时重连；重连不再打 loading，避免占位文案与右上角转圈反复出现
  if (reconnectAttempts.value === 0) {
    loading.value = true
  }
  eventSource = new EventSource(url)
  eventSource.onopen = () => {
    loading.value = false
    reconnectAttempts.value = 0
  }
  eventSource.addEventListener('init', (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data || '{}')
      applyInit(data)
    } catch (_e) {
      //
    }
  })
  eventSource.addEventListener('fragment', (e: MessageEvent) => {
    try {
      const payload = JSON.parse(e.data || '{}')
      const data = payload.data ?? payload
      if (data && (data.source_text !== undefined || data.translations)) {
        fragments.value = [...fragments.value, data].slice(-MAX_DISPLAY_LINES)
      }
    } catch (_e) {
      //
    }
  })
  eventSource.addEventListener('subtitle', (e: MessageEvent) => {
    try {
      const payload = JSON.parse(e.data || '{}')
      const data = (payload.data ?? payload) as Record<string, unknown>
      if (data && typeof data === 'object') {
        subtitleStyle.value = {
          ...subtitleStyle.value,
          ...data,
          backgroundImagePC: (data.backgroundImagePC ?? data.background_image_pc ?? '') as string,
          backgroundImageH5: (data.backgroundImageH5 ?? data.background_image_h5 ?? '') as string
        }
      }
    } catch (_e) {
      //
    }
  })
  eventSource.addEventListener('languages', (e: MessageEvent) => {
    try {
      const payload = JSON.parse(e.data || '{}')
      const data = payload.data ?? payload
      if (data && typeof data === 'object') {
        if (data.sourceLanguage != null) {
          sourceLanguageObj.value = normalizeSourceLanguage(data.sourceLanguage)
        }
        if (data.targetLanguages != null) {
          const list = normalizeTargetLanguages(data.targetLanguages)
          targetLanguageList.value = list
          if (list.length) {
            const stillValid = list.some((t) => t.languageCode === selectedTargetCode.value)
            if (!stillValid) selectedTargetCode.value = list[0].languageCode
          }
        }
        fragments.value = []
        previewLive.value = null
      }
    } catch (_e) {
      //
    }
  })
  eventSource.addEventListener('preview', (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data || '{}') as {
        source_text?: string
        translations?: Record<string, string>
      }
      previewLive.value = isPreviewPayloadEmpty(data) ? null : data
    } catch (_e) {
      //
    }
  })
  eventSource.onerror = () => {
    loading.value = false
    eventSource?.close()
    eventSource = null
    scheduleReconnect()
  }
}

const feedApi = {
  applyInit,
  isPreviewPayloadEmpty,
  fragments,
  previewLive,
  loading,
  applySessionSnapshot: (session: CrV3SessionSnapshot) => {
    postClassSession.value = session
  },
}

if (props.meetingCode && props.h5Participant) {
  useCrV3H5Feed(props.meetingCode, feedApi, { selectedTargetCode })
} else if (props.meetingCode) {
  useCrV3ViewerFeed(props.meetingCode, feedApi)
}

onMounted(() => {
  updateMobileView()
  window.addEventListener('resize', updateMobileView)
  if (!props.meetingCode && props.userCode) {
    connectSSE()
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', updateMobileView)
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (eventSource) {
    eventSource.close()
    eventSource = null
  }
})

</script>

<style scoped>
.translation-whiteboard {
  position: fixed;
  inset: 0;
  background: #ffffff;
  /* PC/H5：全页不滚动；正文区由内部 flex + overflow:hidden 裁切 */
  overflow-x: hidden;
  overflow-y: hidden;
  /* PC：左右留少量边距；H5 由 .mobile-view 覆盖为 0 */
  padding: 32px clamp(12px, 2.5vw, 28px);
  background-color: var(--subtitle-bg, #fff);
  background-image: var(--subtitle-bg-image, none);
  background-size: cover;
  background-position: center;
  color: var(--subtitle-color, rgba(0, 0, 0, 0.85));
  font-size: var(--subtitle-size, 28px);
  font-family: var(--subtitle-font, sans-serif);
  opacity: var(--subtitle-opacity, 1);
}

/* H5：整页列布局，顶部语言栏占位，下方独立滚动区，避免语言按钮盖住正文 */
.translation-whiteboard.mobile-view {
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  box-sizing: border-box;
}

.viewer-lang-tabs {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  padding-top: max(12px, env(safe-area-inset-top, 0px));
  padding-left: max(16px, env(safe-area-inset-left, 0px));
  padding-right: max(16px, env(safe-area-inset-right, 0px));
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
}

/* H5：占满语种栏以下区域；内部再用 vh/flex 裁切，无整页滚动条 */
.mobile-body-panel {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 12px 16px 24px;
  padding-bottom: max(24px, env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
}

.viewer-h5-table {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.viewer-rows--h5-single {
  gap: 8px;
}

/* H5 语种按钮：苹果设计风格 */
.lang-tab {
  min-height: 36px;
  padding: 8px 18px;
  font-size: 15px;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif;
  border: none;
  border-radius: 10px;
  background: rgba(142, 142, 147, 0.12);
  color: rgba(0, 0, 0, 0.85);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.1s ease;
  -webkit-tap-highlight-color: transparent;
}

.lang-tab:active {
  transform: scale(0.98);
}

.lang-tab.active {
  background: #007aff;
  color: #fff;
}

.lang-tab.active:active {
  background: #0056b3;
}

.viewer-cols {
  display: grid;
  gap: 0 16px;
  align-items: start;
  margin: 0 auto;
}

/* PC 观众端：占满可用宽度，各目标语列 1fr 均分，不再限制 1600px 导致两侧大片留白 */
.viewer-table {
  display: flex;
  flex-direction: column;
  margin: 0;
  width: 100%;
  max-width: 100%;
  height: min(88vh, calc(100dvh - 96px));
  min-height: 0;
  box-sizing: border-box;
}

.viewer-rows-clip {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.viewer-cols-header {
  display: grid;
  gap: 0 16px;
  align-items: center;
  margin-bottom: 8px;
}

.viewer-col {
  min-width: 0;
  padding: 12px 16px;
}

.viewer-col-header {
  font-weight: 600;
  color: #1890ff;
  margin-bottom: 8px;
  font-size: 14px;
}

.viewer-rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
}

.viewer-row {
  display: grid;
  gap: 0 16px;
  align-items: stretch;
  min-width: 0;
}

.viewer-line {
  font-size: 1em;
  line-height: var(--subtitle-line-height, 1.5);
  color: inherit;
  margin-bottom: 6px;
  word-break: break-word;
}

/* 识别中临时行：颜色跟随字幕设定（--subtitle-color），不用固定绿色 */
.viewer-row--live .viewer-line--live,
.viewer-line--live {
  color: inherit;
  font-weight: 500;
}


.loading-overlay {
  position: fixed;
  top: 24px;
  right: 24px;
}
</style>
